import type { ParticipationValue } from './participation.constants.js';

/**
 * Homepage age stages (docs/prd-homepage-redesign.md §3.2, §3.3).
 * A stage is the half-open range [lo, hi); activity and Pick age ranges are inclusive.
 */
export const STAGE_KEYS = ['s0', 's1', 's2', 's3', 's4'] as const;
export type StageKey = (typeof STAGE_KEYS)[number];

/** The "给家长" option at the end of the age selector. */
export const ADULTS_KEY = 'adults' as const;
export type AudienceKey = StageKey | typeof ADULTS_KEY;

export const DEFAULT_STAGE: StageKey = 's2';

export interface AgeStage {
  key: StageKey;
  lo: number;
  /** Exclusive upper bound; Infinity for the last stage. */
  hi: number;
  /** Upper bound used for the "full fit" test (s4 counts as 8-12). */
  fullHi: number;
}

export const AGE_STAGES: readonly AgeStage[] = [
  { key: 's0', lo: 0, hi: 2, fullHi: 2 },
  { key: 's1', lo: 2, hi: 3, fullHi: 3 },
  { key: 's2', lo: 3, hi: 5, fullHi: 5 },
  { key: 's3', lo: 5, hi: 8, fullHi: 8 },
  { key: 's4', lo: 8, hi: Infinity, fullHi: 12 },
];

export function isStageKey(v: unknown): v is StageKey {
  return typeof v === 'string' && (STAGE_KEYS as readonly string[]).includes(v);
}

export function getStage(key: StageKey): AgeStage {
  return AGE_STAGES.find((s) => s.key === key)!;
}

export interface StageMatchable {
  ageMin: number;
  ageMax: number;
  participation: ParticipationValue;
}

/**
 * S1: an item is in an age range when children take part and the inclusive item
 * range overlaps the half-open [lo, hi). Used by the homepage and by the
 * All Activities ageMin/ageMax range so both return the same set.
 */
export function rangeMatches(item: StageMatchable, lo: number, hi: number = Infinity): boolean {
  if (item.participation === 'ADULT_ONLY') return false;
  return item.ageMin < hi && item.ageMax >= lo;
}

export function stageMatches(item: StageMatchable, stage: AgeStage): boolean {
  return rangeMatches(item, stage.lo, stage.hi);
}

/** S3: the item covers every age in the stage (s4 measured up to 12). */
export function isFullFit(item: StageMatchable, stage: AgeStage): boolean {
  return item.ageMin <= stage.lo && item.ageMax >= stage.fullHi;
}
